import clsx from 'clsx';
import Image from 'next/image';
import Link from 'next/link';
import { DesktopMenu } from '~/components/layouts/DesktopMenu';
import { MobileMenu } from '~/components/layouts/MobileMenu';
import {
  GET_ALLSTAR_MENU_QUERY,
  GET_ARTICLE_BANNER_CONTENT,
  GET_HOMEPAGE_BANNER_CONTENT,
  GET_MENUS_QUERY,
} from '~/queries/wpQueries';
import type { MenuItem } from '~/types/graphql';
import { getColors } from '~/utils/getColors';
import { getLogo } from '~/utils/getLogo';
import { gqlFetch } from '~/utils/gqlFetch';
import { Search } from './Search';

import { Suspense } from 'react';
import mobileLogo from '~/assets/img/ico-ausl.svg';
import desktopLogo from '~/assets/img/logo-ausl.svg';
import { Banner } from '~/components/blocks/Banner';

const teams = ['bandits', 'blaze', 'talons', 'volts'];

export async function Header() {
  const { data, status } = await gqlFetch({
    query: GET_MENUS_QUERY,
  });

  if (status !== 200) {
    return null;
  }

  const { data: homepageBannerData } = await gqlFetch({
    query: GET_HOMEPAGE_BANNER_CONTENT,
  });

  const { data: articleBannerData } = await gqlFetch({
    query: GET_ARTICLE_BANNER_CONTENT,
  });

  const { data: allStarData } = await gqlFetch({
    query: GET_ALLSTAR_MENU_QUERY,
  });

  return (
    <>
      {homepageBannerData?.acfSiteSettings?.siteSettingsGroup?.homepageTopBanner
        ?.bannerImage && (
        <Banner
          id="top-banner-home"
          className="hidden"
          {...homepageBannerData.acfSiteSettings.siteSettingsGroup
            .homepageTopBanner}
        />
      )}
      {articleBannerData?.acfSiteSettings?.siteSettingsGroup?.articleTopBanner
        ?.bannerImage && (
        <Banner
          id="top-banner-article"
          className="hidden"
          {...articleBannerData.acfSiteSettings.siteSettingsGroup
            .articleTopBanner}
        />
      )}
      <header
        className={clsx(
          'relative top-0 z-200 w-full bg-black font-roboto text-[--theme-action] lg:px-8 lg:py-4',
        )}
      >
        {/* Desktop Menu */}
        <div className="hidden h-[120px] items-end justify-between rounded-lg bg-[--theme-bg] p-2 lg:flex">
          <div className="group relative">
            <div className="flex items-end gap-x-4 overflow-hidden rounded-lg px-4 py-2.5">
              <Link href="/">
                <Image src={desktopLogo} alt="AUSL" />
              </Link>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="12"
                height="7"
                viewBox="0 0 12 7"
                fill="none"
                className="stroke-current transition-transform duration-300 group-hover:rotate-180"
              >
                <path
                  d="M1.51758 0.979492L6.09794 5.97949L10.6783 0.979492"
                  strokeWidth="1.52679"
                  strokeLinejoin="round"
                />
              </svg>
            </div>

            <ul className="absolute -inset-x-2 top-full z-10 flex max-h-0 flex-col gap-2 overflow-hidden rounded-b-lg bg-[--theme-bg] px-2 transition-[max-height] duration-300 group-hover:max-h-screen">
              {teams.map((item, i) => (
                <li key={i} className="h-[75px] first:mt-2 last:mb-2">
                  <TeamLogo team={item} />
                </li>
              ))}
            </ul>
          </div>

          <DesktopMenu
            items={data.headerMenuItems.nodes}
            allStarCupMenuItems={allStarData.allStarCupMenuItems.nodes}
          />
        </div>

        {/* Mobile Menu */}
        <div className="flex items-center justify-center bg-[#0f2943] p-5 lg:hidden">
          <MobileMenu
            items={data.headerMenuItems.nodes}
            allStarCupMenuItems={allStarData.allStarCupMenuItems.nodes}
          />

          <Link href="/">
            <Image src={mobileLogo} alt="AUSL" />
          </Link>
        </div>

        <Suspense>
          <Search />
        </Suspense>

        <div className="flex items-center justify-center bg-[#0f2943] px-5 pb-5 pt-[5px] lg:hidden">
          <nav className="mx-auto w-full max-w-[275px]">
            <ul className="flex w-full justify-between text-[16px] font-semibold leading-[11px]">
              {(data.mobileMenuItems.nodes as MenuItem[])
                .slice(0, 3)
                .map((item, i) => (
                  <li key={i}>
                    <Link href={item.uri ?? ''}>{item.label}</Link>
                  </li>
                ))}
            </ul>
          </nav>
        </div>
      </header>
    </>
  );
}

export function TeamLogo({
  team,
}: {
  team: string;
  mobile?: boolean;
  desktop?: boolean;
}) {
  const { logo } = getLogo(team) || {};
  const { primary } = getColors(team);

  return (
    <Link
      href={`/${team}`}
      className="flex h-full items-center justify-center rounded px-4"
      style={{ backgroundColor: primary }}
    >
      <Image src={logo} alt={team} />
    </Link>
  );
}
